import { useEffect, useRef } from 'react';

/**
 * Drives the browser tab favicon for unread Bonusly + Bizy notifications.
 *
 * While mounted, the hook always renders one of two inline-SVG favicons:
 *   - Clean: the Bonusly green "B" mark.
 *   - Badged: the same mark with a red dot in the upper-right corner.
 *
 * It deliberately does *not* depend on whatever favicon the host page
 * shipped — earlier versions tried to "restore the original" on the no-unread
 * transition, but the SPA `index.html` has no `<link rel="icon">`, so the
 * restore step removed the badged link without adding anything back and
 * browsers kept the cached badged favicon painted in the tab strip.
 *
 * Implementation notes:
 *   - We replace the `<link rel="icon">` element on every change rather than
 *     mutating its `href`. Chrome especially won't repaint the tab when only
 *     the attribute changes; appending a fresh element forces a re-evaluation.
 *   - On unmount we capture the original page favicon and restore it (or
 *     simply remove our element if there was none) so the badge doesn't
 *     leak into post-logout pages.
 */

const ICON_LINK_SELECTOR = "link[rel~='icon']";
const FAVICON_TYPE = 'image/svg+xml';

const BONUSLY_LOGO_PATH = `<path fill="#22b94f" d="m62.0321 85.3804c2.7609-.3524 5.1987 1.7329 5.1987 4.5232v80.2134c0 85.441 4.3176 229.947 152.6422 229.947 91.374 0 152.79-73.193 152.79-158.986 0-85.794-73.869-149.5296-165.831-149.5296-11.278 0-20.413 9.1346-20.413 20.4126v55.6s22.058-.058 26.699-.058c62.619 0 83.855 47.052 83.855 73.575 0 49.725-32.808 82.357-73.693 82.357-51.076 0-80.565-40.826-80.565-82.974v-177.8727c.029-23.5263 19.091-42.5883 42.588-42.5883 261.963 0 326.697 100.009 326.697 166.858 0 169.884-196.288 304.169-323.936 304.169-155.3738 0-188.064-145.417-188.064-250.948 0-119.688 35.6567-151.174 62.0321-154.6986z"/>`;

const CLEAN_FAVICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">${BONUSLY_LOGO_PATH}</svg>`;

const BADGED_FAVICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">${BONUSLY_LOGO_PATH}<circle cx="396" cy="116" r="116" fill="#fff"/><circle cx="396" cy="116" r="92" fill="#e53e3e"/></svg>`;

const CLEAN_FAVICON_HREF = `data:image/svg+xml;utf8,${encodeURIComponent(CLEAN_FAVICON_SVG)}`;
const BADGED_FAVICON_HREF = `data:image/svg+xml;utf8,${encodeURIComponent(BADGED_FAVICON_SVG)}`;

function setIconLink(href: string, type: string): void {
	document.querySelectorAll<HTMLLinkElement>(ICON_LINK_SELECTOR).forEach((el) => el.remove());
	const link = document.createElement('link');
	link.rel = 'icon';
	link.setAttribute('href', href);
	link.setAttribute('type', type);
	document.head.appendChild(link);
}

export function useNotificationFavicon(hasUnread: boolean): void {
	const originalHrefRef = useRef<string | null>(null);
	const originalTypeRef = useRef<string | null>(null);
	const hasCapturedOriginalRef = useRef(false);

	useEffect(() => {
		if (typeof document === 'undefined') return;

		if (!hasCapturedOriginalRef.current) {
			const existing = document.querySelector<HTMLLinkElement>(ICON_LINK_SELECTOR);
			originalHrefRef.current = existing?.getAttribute('href') ?? null;
			originalTypeRef.current = existing?.getAttribute('type') ?? null;
			hasCapturedOriginalRef.current = true;
		}

		setIconLink(hasUnread ? BADGED_FAVICON_HREF : CLEAN_FAVICON_HREF, FAVICON_TYPE);
	}, [hasUnread]);

	useEffect(() => {
		return () => {
			if (!hasCapturedOriginalRef.current) return;

			document.querySelectorAll<HTMLLinkElement>(ICON_LINK_SELECTOR).forEach((el) => el.remove());

			if (originalHrefRef.current === null) return;

			const link = document.createElement('link');
			link.rel = 'icon';
			link.setAttribute('href', originalHrefRef.current);
			if (originalTypeRef.current !== null) {
				link.setAttribute('type', originalTypeRef.current);
			}
			document.head.appendChild(link);
		};
	}, []);
}
